import { useRef, useState } from "react";

import { FloatingPortal } from "@floating-ui/react";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Chat } from "src/base-components/Chat/Chat";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { CopilotTitle } from "src/copilot/CopilotTitle";
import { PromptBox } from "src/copilot/PromptBox";
import { TitleHelpContent } from "src/copilot/TitleHelpContent";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

type MockMessage = {
  role: "user" | "assistant";
  content: string;
};

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "What is the source of the funds in question?",
    prompt: "What is the source of the funds in question?",
  },
  {
    label: "Have there been any unusual transactions recently?",
    prompt: "Have there been any unusual transactions recently?",
  },
  {
    label:
      "Find connections between the account holder and known criminal entities",
    prompt:
      "Find connections between the account holder and known criminal entities",
  },
];

const MOCK_RESPONSES = [
  "Based on the case data, here's a summary of the key details and current status. The case appears to be progressing through the standard workflow with all required information collected.",
  "Looking at the case history, several actions have been recorded including initial review, data validation, and risk assessment steps.",
  "For next steps, I'd recommend reviewing the outstanding items, verifying the latest data points, and proceeding with the approval workflow if all criteria are met.",
];

const TitleRight: React.FC<{ onReset: VoidFunction }> = ({ onReset }) => (
  <>
    <span className="ml-auto text-gray-200">|</span>
    <Tooltip placement="bottom" title="Start a new chat" asChild>
      <IconButton icon={faPlus} size="xs" onClick={onReset} />
    </Tooltip>
  </>
);

export const CaseCopilotWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
}) => {
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const { close: closeWindow } = useFloatingWindowsActions();
  const [messages, setMessages] = useState<MockMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const listRef = useRef<HTMLUListElement>(null);
  const mockIndexRef = useRef(0);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
  };

  const sendMessage = (text: string) => {
    const userMessage: MockMessage = { role: "user", content: text };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt("");
    setIsLoading(true);
    scrollToBottom();

    setTimeout(() => {
      const responseIndex = mockIndexRef.current % MOCK_RESPONSES.length;
      mockIndexRef.current += 1;
      const assistantMessage: MockMessage = {
        role: "assistant",
        content: MOCK_RESPONSES[responseIndex],
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);
      scrollToBottom();
    }, 1500);
  };

  const handleReset = () => {
    setMessages([]);
    setPrompt("");
    setIsLoading(false);
    mockIndexRef.current = 0;
  };

  const handleClose = () => {
    closeWindow(id);
  };

  return (
    <FloatingPortal>
      <FloatingWindowInternal
        ref={windowRef}
        clipOverflow={false}
        closeIcon={faMinus}
        height="h-150"
        helpText={
          <TitleHelpContent description="Ask questions about this case and get help with analysis, actions, and next steps." />
        }
        isPinned={false}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title={<CopilotTitle />}
        titleRight={<TitleRight onReset={handleReset} />}
        draggable
        onClose={handleClose}
      >
        <Chat.Body>
          {messages.length > 0 ? (
            <Chat.MessageList ref={listRef}>
              {messages.map((message, index) => (
                <Chat.Message
                  key={`${message.role}-${index}`}
                  role={message.role}
                >
                  <Chat.TextContent
                    animate={false}
                    content={message.content}
                    delay={0}
                  />
                </Chat.Message>
              ))}
              {isLoading && <Chat.LoadingIndicator mode="loading" />}
            </Chat.MessageList>
          ) : (
            <Chat.EmptyState
              prompts={SUGGESTION_PROMPTS}
              onPromptClick={sendMessage}
            />
          )}
          <Chat.InputArea>
            <PromptBox
              disabled={isLoading || prompt.trim().length === 0}
              errored={false}
              mentionTextPlaceholder=""
              minified={messages.length > 0}
              resource={[]}
              resourceType={
                FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
              }
              value={prompt}
              onChange={setPrompt}
              onReset={handleReset}
              onSubmit={(content) => {
                const trimmed = content.trim();
                if (trimmed) {
                  sendMessage(trimmed);
                }
              }}
            />
          </Chat.InputArea>
        </Chat.Body>
      </FloatingWindowInternal>
    </FloatingPortal>
  );
};
